import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsAuthBootstrapStatus } from "@cmts/auth/authBootstrap.js";
import type { CmtsStoredSession } from "@cmts/auth/authStore.js";
import { readCmtsSession } from "@cmts/auth/authStore.js";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { serializeReduxError } from "./thunkExtra.js";

type SessionSnapshot = Omit<CmtsStoredSession, "accessToken" | "refreshToken" | "permissions" | "roles" | "roleIds" | "permissionCodes" | "departmentIds" | "businessUnitIds" | "siteIds" | "customerIds" | "supplierIds" | "organizationUnitIds"> & {
  accessTokenPresent: boolean;
  refreshTokenPresent: boolean;
  expiresAtUtc?: string | undefined;
  permissions?: string[] | undefined;
  roles?: string[] | undefined;
  roleIds?: string[] | undefined;
  permissionCodes?: string[] | undefined;
  departmentIds?: string[] | undefined;
  businessUnitIds?: string[] | undefined;
  siteIds?: string[] | undefined;
  customerIds?: string[] | undefined;
  supplierIds?: string[] | undefined;
  organizationUnitIds?: string[] | undefined;
};

export type CmtsAuthState = {
  readonly session: SessionSnapshot | null;
  readonly status: "anonymous" | "authenticated" | "loading" | "refreshing" | "loggingOut";
  readonly error?: CmtsSerializableError | undefined;
  readonly lastLoginAtUtc?: string | undefined;
  readonly lastLogoutAtUtc?: string | undefined;
  readonly lastRefreshAtUtc?: string | undefined;
  readonly bootstrapStatus: CmtsAuthBootstrapStatus;
};

const initialState: CmtsAuthState = {
  session: null,
  status: "anonymous",
  bootstrapStatus: "idle",
};

function copyOptionalArray(values: readonly string[] | undefined): string[] | undefined {
  return values ? [...values] : undefined;
}

function toSnapshot(session: CmtsStoredSession | null): SessionSnapshot | null {
  if (!session) return null;
  return {
    userId: session.userId,
    tenantId: session.tenantId,
    tenantCode: session.tenantCode,
    tenantName: session.tenantName,
    organizationUnitId: session.organizationUnitId,
    organizationUnitIds: copyOptionalArray(session.organizationUnitIds),
    displayName: session.displayName,
    email: session.email,
    persona: session.persona,
    expiresAtUtc: session.expiresAtUtc,
    accessTokenPresent: Boolean(session.accessToken),
    refreshTokenPresent: Boolean(session.refreshToken),
    permissions: copyOptionalArray(session.permissions),
    roles: copyOptionalArray(session.roles),
    roleIds: copyOptionalArray(session.roleIds),
    permissionCodes: copyOptionalArray(session.permissionCodes),
    departmentIds: copyOptionalArray(session.departmentIds),
    businessUnitIds: copyOptionalArray(session.businessUnitIds),
    siteIds: copyOptionalArray(session.siteIds),
    customerIds: copyOptionalArray(session.customerIds),
    supplierIds: copyOptionalArray(session.supplierIds),
    isExternalUser: session.isExternalUser,
    externalPartyType: session.externalPartyType,
    confidentialityClearance: session.confidentialityClearance,
    runtimeMode: session.runtimeMode,
  };
}

export const refreshCmtsToken = createAsyncThunk<CmtsStoredSession | null, void, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "cmtsAuth/refreshToken",
  async (_arg, thunkApi) => {
    try {
      if (!thunkApi.extra.authRepository) {
        return thunkApi.rejectWithValue({ message: "Auth repository is not available for token refresh.", atUtc: new Date().toISOString() });
      }
      await thunkApi.extra.authRepository.refresh();
      return readCmtsSession();
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Token refresh failed."));
    }
  },
);

export const fetchCurrentCmtsUser = createAsyncThunk<CmtsStoredSession | null, void, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "cmtsAuth/fetchCurrentUser",
  async (_arg, thunkApi) => {
    try {
      if (!thunkApi.extra.authRepository) {
        return thunkApi.rejectWithValue({ message: "Auth repository is not available for current-user refresh.", atUtc: new Date().toISOString() });
      }
      await thunkApi.extra.authRepository.me();
      return readCmtsSession();
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Current-user refresh failed."));
    }
  },
);

export const logoutCmtsSession = createAsyncThunk<void, void, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "cmtsAuth/logout",
  async (_arg, thunkApi) => {
    try {
      await thunkApi.extra.authRepository?.logout();
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Logout failed."));
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    sessionHydrated(state, action: PayloadAction<CmtsStoredSession | null>) {
      state.session = toSnapshot(action.payload);
      state.status = action.payload?.accessToken ? "authenticated" : "anonymous";
      state.bootstrapStatus = action.payload?.accessToken ? "restoring" : "unauthenticated";
      state.error = undefined;
    },
    loginStarted(state) {
      state.status = "loading";
      state.bootstrapStatus = "idle";
      state.error = undefined;
    },
    loginBootstrapStarted(state) {
      if (state.session?.accessTokenPresent) state.status = "authenticated";
      state.bootstrapStatus = "restoring";
      state.error = undefined;
    },
    loginSucceeded(state, action: PayloadAction<CmtsStoredSession>) {
      state.session = toSnapshot(action.payload);
      state.status = "authenticated";
      state.bootstrapStatus = "restoring";
      state.error = undefined;
      state.lastLoginAtUtc = new Date().toISOString();
    },
    loginFailed(state, action: PayloadAction<CmtsSerializableError>) {
      state.status = "anonymous";
      state.bootstrapStatus = "failed";
      state.error = action.payload;
    },
    refreshStarted(state) {
      state.status = "refreshing";
      state.error = undefined;
    },
    refreshSucceeded(state, action: PayloadAction<CmtsStoredSession>) {
      state.session = toSnapshot(action.payload);
      state.status = "authenticated";
      state.bootstrapStatus = "authenticated";
      state.error = undefined;
      state.lastRefreshAtUtc = new Date().toISOString();
    },
    tokenRefreshFailed(state, action: PayloadAction<CmtsSerializableError | undefined>) {
      state.session = null;
      state.status = "anonymous";
      state.bootstrapStatus = "failed";
      state.error = action.payload;
      state.lastLogoutAtUtc = new Date().toISOString();
    },
    loginBootstrapSucceeded(state) {
      state.bootstrapStatus = "authenticated";
      state.error = undefined;
    },
    loginBootstrapFailed(state, action: PayloadAction<CmtsSerializableError>) {
      state.bootstrapStatus = "failed";
      state.error = action.payload;
    },

    bootstrapUnauthenticated(state) {
      state.session = null;
      state.status = "anonymous";
      state.bootstrapStatus = "unauthenticated";
      state.error = undefined;
    },
    logoutSucceeded(state) {
      state.session = null;
      state.status = "anonymous";
      state.bootstrapStatus = "unauthenticated";
      state.error = undefined;
      state.lastLogoutAtUtc = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshCmtsToken.pending, (state) => {
        state.status = "refreshing";
        state.error = undefined;
      })
      .addCase(refreshCmtsToken.fulfilled, (state, action) => {
        state.session = toSnapshot(action.payload);
        state.status = action.payload?.accessToken ? "authenticated" : "anonymous";
        state.bootstrapStatus = action.payload?.accessToken ? "restoring" : "unauthenticated";
        state.lastRefreshAtUtc = new Date().toISOString();
      })
      .addCase(refreshCmtsToken.rejected, (state, action) => {
        state.session = null;
        state.status = "anonymous";
        state.bootstrapStatus = "failed";
        state.error = action.payload ?? serializeReduxError(action.error, "Token refresh failed.");
      })
      .addCase(fetchCurrentCmtsUser.pending, (state) => {
        if (state.status !== "authenticated") state.status = "loading";
        state.error = undefined;
      })
      .addCase(fetchCurrentCmtsUser.fulfilled, (state, action) => {
        state.session = toSnapshot(action.payload);
        state.status = action.payload?.accessToken ? "authenticated" : "anonymous";
        state.bootstrapStatus = action.payload?.accessToken ? "authenticated" : state.bootstrapStatus;
      })
      .addCase(fetchCurrentCmtsUser.rejected, (state, action) => {
        state.error = action.payload ?? serializeReduxError(action.error, "Current-user refresh failed.");
        if (!state.session) state.status = "anonymous";
      })
      .addCase(logoutCmtsSession.pending, (state) => {
        state.status = "loggingOut";
      })
      .addCase(logoutCmtsSession.fulfilled, (state) => {
        state.session = null;
        state.status = "anonymous";
        state.bootstrapStatus = "unauthenticated";
        state.error = undefined;
        state.lastLogoutAtUtc = new Date().toISOString();
      })
      .addCase(logoutCmtsSession.rejected, (state, action) => {
        state.session = null;
        state.status = "anonymous";
        state.bootstrapStatus = "unauthenticated";
        state.error = action.payload ?? serializeReduxError(action.error, "Logout failed after clearing local session state.");
        state.lastLogoutAtUtc = new Date().toISOString();
      });
  },
});

export const authActions = authSlice.actions;
export const authReducer = authSlice.reducer;
